import React from "react";
import type { Property } from "../PropiedadesPageNew";

interface PropertyCardProps {
    prop: Property;
    isSelected: boolean;
    activeView: string;
    onClick: (prop: Property) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ prop, isSelected, activeView, onClick }) => {
    // Helper visual
    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'AC': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
            case 'PE': return 'bg-amber-100 text-amber-700 border border-amber-200';
            case 'SU': return 'bg-red-100 text-red-700 border border-red-200';
            default: return 'bg-gray-100 text-gray-700 border border-gray-200';
        }
    };

    return (
        <div
            onClick={() => onClick(prop)}
            className={`bg-white border rounded-xl overflow-hidden cursor-pointer transition-all duration-200 
                ${isSelected ? "border-[#4f6df5] ring-2 ring-[#4f6df5]/20 shadow-md" : "border-gray-200 hover:border-[#4f6df5] hover:-translate-y-0.5 hover:shadow-lg"}
                ${activeView === 'list' ? 'flex items-center' : 'flex flex-col'}
            `}
        >
            {/* PARTE SUPERIOR DE LA TARJETA (IMAGEN O COLOR) */}
            <div className={`relative flex items-center justify-center bg-gradient-to-br ${prop.gradient} ${activeView === 'list' ? 'w-32 h-[90px] shrink-0' : 'h-[130px]'}`}>
                {prop.mainPhotoUrl ? (
                    <img 
                        src={prop.mainPhotoUrl} 
                        alt={prop.name} 
                        className="w-full h-full object-cover" 
                    />
                ) : (
                    <span className="text-[40px] text-gray-500/50">{prop.icon}</span>
                )}
                
                {/* BADGE DE ESTADO */}
                <div className="absolute top-2 right-2">
                    <span className={`text-[10px] font-bold px-2 py-[3px] rounded-md uppercase tracking-[0.5px] backdrop-blur-[4px] ${getStatusBadgeColor(prop.status)}`}>
                        {prop.statusLabel}
                    </span>
                </div>
            </div>

            <div className={`p-3.5 flex-1 ${activeView === 'list' ? 'flex justify-between items-center' : ''}`}>
                <div className={activeView === 'list' ? 'flex-1 min-w-0 pr-4' : ''}>
                    <div className="text-[14px] font-bold mb-1 text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis" title={prop.name}>{prop.name}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-1 mb-2.5" title={prop.location}>
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">{prop.location}</span>
                    </div>
                </div>

                <div className={`flex ${activeView === 'list' ? 'gap-6 shrink-0' : 'justify-between pt-2.5 border-t border-gray-100'}`}>
                    <div className="text-center">
                        <div className="font-mono text-[13px] font-bold text-emerald-600">{prop.currency} {prop.price}</div>
                        <div className="text-[10px] text-gray-500">/ noche</div>
                    </div>
                    <div className="text-center">
                        <div className="font-mono text-[13px] font-bold text-gray-700">{prop.bookings}</div>
                        <div className="text-[10px] text-gray-500">reservas</div>
                    </div>
                    <div className="text-center">
                        <div className={`font-mono text-[13px] font-bold ${prop.rating ? 'text-amber-500' : 'text-gray-400'}`}>{prop.rating ? `${prop.rating} ★` : '—'}</div>
                        <div className="text-[10px] text-gray-500">rating</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyCard;